import type { FormInstance } from 'antd';
import {
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Switch,
} from 'antd';
import type { Dayjs } from 'dayjs';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  CalendarRelationFields,
  type CalendarRelationFormValue,
} from '@/modules/zy-crm/components/calendar-relation-fields';
import { queryDictionaryItemOptions } from '@/services/bff/dictionary-management.api';

export const DEFAULT_SCHEDULE_EVENT_TYPE = 'my_schedule';
const SCHEDULE_TYPE_DICTIONARY_CODE = 'schedule-types';

export type CalendarEventFormValues = {
  title?: string;
  eventType?: string;
  startTime?: Dayjs;
  endTime?: Dayjs;
  allDay?: boolean;
  location?: string;
  description?: string;
  color?: string;
  ownerMemberUuid?: string;
  participantMemberUuids?: string[];
  relations?: CalendarRelationFormValue[];
};

type CalendarEventFormProps = {
  form: FormInstance<CalendarEventFormValues>;
  readOnly?: boolean;
  colorOptions?: Array<{ value: string; label: string }>;
  colorOptionRender?: (option: { value: string; label: string }) => ReactNode;
  showColorField?: boolean;
  showOwnerMemberField?: boolean;
  ownerMemberField?: ReactNode;
  showParticipantMemberField?: boolean;
  participantMemberField?: ReactNode;
};

export function CalendarEventForm({
  form,
  readOnly = false,
  colorOptions = [],
  colorOptionRender,
  showColorField = false,
  showOwnerMemberField = false,
  ownerMemberField,
  showParticipantMemberField = false,
  participantMemberField,
}: CalendarEventFormProps) {
  const [eventTypeOptions, setEventTypeOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [eventTypeLoading, setEventTypeLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setEventTypeLoading(true);
    void queryDictionaryItemOptions(SCHEDULE_TYPE_DICTIONARY_CODE)
      .then((list) => {
        if (!active) return;
        setEventTypeOptions(
          (list || []).map((item) => ({
            value: item.value,
            label: item.label,
          })),
        );
      })
      .catch((error: any) => {
        if (!active) return;
        message.error(error?.message || '日程类型加载失败，请稍后重试');
      })
      .finally(() => {
        if (!active) return;
        setEventTypeLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const mergedEventTypeOptions = useMemo(() => {
    const currentValue = String(form.getFieldValue('eventType') || '').trim();
    if (!currentValue) {
      return eventTypeOptions;
    }
    const matched = eventTypeOptions.some(
      (item) => item.value === currentValue,
    );
    if (matched) {
      return eventTypeOptions;
    }
    return [
      ...eventTypeOptions,
      {
        value: currentValue,
        label: currentValue,
      },
    ];
  }, [eventTypeOptions, form]);

  return (
    <Form form={form} layout="vertical">
      <Row gutter={12}>
        <Col xs={24}>
          <Form.Item
            name="title"
            label="日程标题"
            rules={[{ required: true, message: '请输入日程标题' }]}
          >
            <Input disabled={readOnly} maxLength={200} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="eventType"
            label="日程类型"
            rules={[{ required: true, message: '请选择日程类型' }]}
          >
            <Select
              disabled={readOnly}
              loading={eventTypeLoading}
              options={mergedEventTypeOptions}
              placeholder="请选择日程类型"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="allDay" label="全天" valuePropName="checked">
            <Switch disabled={readOnly} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="startTime"
            label="开始时间"
            rules={[{ required: true, message: '请选择开始时间' }]}
          >
            <DatePicker
              disabled={readOnly}
              style={{ width: '100%' }}
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="endTime" label="结束时间">
            <DatePicker
              disabled={readOnly}
              style={{ width: '100%' }}
              showTime
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="location" label="地点">
            <Input
              disabled={readOnly}
              maxLength={255}
              placeholder="请输入地点"
            />
          </Form.Item>
        </Col>

        {showColorField ? (
          <Col xs={24} md={12}>
            <Form.Item name="color" label="颜色">
              <Select
                disabled={readOnly}
                options={colorOptions}
                optionRender={
                  colorOptionRender
                    ? (option) =>
                        colorOptionRender({
                          value: String(option.value),
                          label: String(option.label),
                        })
                    : undefined
                }
              />
            </Form.Item>
          </Col>
        ) : null}

        <Col xs={24}>
          <Form.Item name="description" label="描述">
            <Input.TextArea disabled={readOnly} rows={4} maxLength={1000} />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <CalendarRelationFields form={form} readOnly={readOnly} />
        </Col>

        {showOwnerMemberField ? (
          <Col xs={24}>
            <Form.Item name="ownerMemberUuid" label="负责人">
              {ownerMemberField}
            </Form.Item>
          </Col>
        ) : null}

        {showParticipantMemberField ? (
          <Col xs={24}>
            <Form.Item
              name="participantMemberUuids"
              label="参与人"
              initialValue={[]}
            >
              {participantMemberField}
            </Form.Item>
          </Col>
        ) : null}
      </Row>
    </Form>
  );
}

export const calendarEventFormDefaultValues = (
  defaults?: Partial<CalendarEventFormValues>,
): CalendarEventFormValues => ({
  title: '',
  eventType: DEFAULT_SCHEDULE_EVENT_TYPE,
  allDay: false,
  participantMemberUuids: [],
  relations: [],
  ...defaults,
});

export {
  createCalendarRelationFormValue,
  normalizeCalendarRelationPayload,
  toCalendarRelationFormValues,
} from '@/modules/zy-crm/components/calendar-relation-fields';
export type { CalendarRelationFormValue };
